import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as constants from '../constants/CONSTANT';
import DataApi from '../api/DataApi';
import './RoleManager.css';

const RoleManager = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [activeLicenses, setActiveLicenses] = useState([]);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    parent_role_id: '',
    company_license_id: '',
    is_active: true,
    is_standard: false
  });
  const [filters, setFilters] = useState({
    name: '',
    is_active: '',
    parent_role_id: ''
  });
  const [parentRoles, setParentRoles] = useState([]);

  // Initialize DataApi for roles
  const roleApi = new DataApi('roles');

  useEffect(() => {
    fetchRoles();
    fetchParentRoles();
  }, [filters]);

  const fetchActiveLicenses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${constants.API_BASE_URL}/api/company-config/active-licenses`,
        { headers: { Authorization: token } }
      );
      if (response?.data?.success && Array.isArray(response.data.data)) {
        setActiveLicenses(response.data.data);
      } else {
        setActiveLicenses([]);
      }
    } catch (err) {
      console.error('Failed to fetch active licenses:', err);
      setActiveLicenses([]);
    }
  };

  useEffect(() => {
    if (showForm) fetchActiveLicenses();
  }, [showForm]);

  const fetchRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.name) params.append('name', filters.name);
      if (filters.is_active !== '') params.append('is_active', filters.is_active);
      if (filters.parent_role_id) params.append('parent_role_id', filters.parent_role_id);

      const response = await roleApi.fetchAll(params.toString());

      if (response.data.success) {
        setRoles(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch roles');
      console.error('Error fetching roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchParentRoles = async () => {
    try {
      const response = await roleApi.fetchAll();

      if (response.data.success) {
        setParentRoles(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching parent roles:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      parent_role_id: '',
      company_license_id: '',
      is_active: true,
      is_standard: false
    });
    setEditingRole(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = { ...formData };
      const selectedLicenseId = payload.company_license_id || null;
      payload.company_license_type_id = selectedLicenseId || null;
      payload.company_license_id = null;

      let response;
      if (editingRole) {
        response = await roleApi.update(payload, editingRole.id);
      } else {
        response = await roleApi.create(payload);
      }

      if (response.data.success) {
        setShowForm(false);
        resetForm();
        fetchRoles();
        alert(editingRole ? 'Role updated successfully!' : 'Role created successfully!');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save role');
      console.error('Error saving role:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    const licenseValue = role.company_license_type_id || role.company_license_id || '';
    setFormData({
      name: role.name,
      parent_role_id: role.parent_role_id || '',
      company_license_id: licenseValue,
      is_active: role.is_active,
      is_standard: role.is_standard
    });
    setShowForm(true);
  };

  useEffect(() => {
    if (!showForm || !editingRole || !activeLicenses.length || !formData.company_license_id) return;
    const match = activeLicenses.find((lic) => lic.id === formData.company_license_id);
    if (match) return;
    const resolved = activeLicenses.find(
      (lic) => Array.isArray(lic.company_license_ids) && lic.company_license_ids.includes(formData.company_license_id)
    );
    if (resolved) setFormData((prev) => ({ ...prev, company_license_id: resolved.id }));
  }, [showForm, editingRole, activeLicenses, formData.company_license_id]);

  const handleViewPermissions = (role) => {
    navigate(`/setup/permission/${role.id}`);
  };

  // Check if role name is admin (case-insensitive)
  const isAdminRole = (roleName) => {
    return roleName && roleName.toLowerCase() === 'admin';
  };

  const clearFilters = () => {
    setFilters({
      name: '',
      is_active: '',
      parent_role_id: ''
    });
  };

  const getParentRoleName = (parentRoleId) => {
    if (!parentRoleId) return 'None';
    const parentRole = parentRoles.find(role => role.id === parentRoleId);
    return parentRole ? parentRole.name : 'Unknown';
  };

  return (
    <div className="role-manager">
      <div className="role-manager-header">
        <h2>Role Management</h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          Create New Role
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-item">
            <label>Name:</label>
            <input
              type="text"
              name="name"
              value={filters.name}
              onChange={handleFilterChange}
              placeholder="Search by name..."
            />
          </div>
          <div className="filter-item">
            <label>Status:</label>
            <select name="is_active" value={filters.is_active} onChange={handleFilterChange}>
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="filter-item">
            <label>Parent Role:</label>
            <select name="parent_role_id" value={filters.parent_role_id} onChange={handleFilterChange}>
              <option value="">All</option>
              {parentRoles.map(role => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>
          <div className="filter-item">
            <button className="btn btn-secondary" onClick={clearFilters}>
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="form-overlay">
          <div className="form-container">
            <div className="form-header">
              <h3>{editingRole ? 'Edit Role' : 'Create New Role'}</h3>
              <button
                className="btn-close"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Role Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter role name"
                  disabled={editingRole && isAdminRole(editingRole.name)}
                />
                {editingRole && isAdminRole(editingRole.name) && (
                  <small className="form-help-text">
                    Admin role name cannot be changed
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Parent Role</label>
                <select
                  name="parent_role_id"
                  value={formData.parent_role_id}
                  onChange={handleInputChange}
                >
                  <option value="">No Parent Role</option>
                  {parentRoles
                    .filter(role => !editingRole || role.id !== editingRole.id)
                    .map(role => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))
                  }
                </select>
              </div>

              <div className="form-group">
                <label>License</label>
                <select
                  name="company_license_id"
                  value={formData.company_license_id}
                  onChange={handleInputChange}
                >
                  <option value="">No License</option>
                  {activeLicenses.map((lic) => (
                    <option key={lic.id} value={lic.id}>
                      {lic.license_type_name || lic.license_type_code || lic.name}
                      {lic.used_count != null && lic.quantity != null ? ` (${lic.used_count}/${lic.quantity})` : ''}
                    </option>
                  ))}
                </select>
                <small className="form-help-text">
                  Licenses from active subscriptions only
                </small>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  Is Active
                </label>
              </div>


              {error && <div className="error-message">{error}</div>}

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (editingRole ? 'Update Role' : 'Create Role')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Roles List */}
      <div className="roles-list">
        {loading && <div className="loading">Loading roles...</div>}

        {error && <div className="error-message">{error}</div>}

        {!loading && roles.length === 0 && (
          <div className="no-data">No roles found</div>
        )}

        {!loading && roles.length > 0 && (
          <div className="table-container">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Parent Role</th>
                  <th>Status</th>
                  <th>Standard</th>
                  <th>Created Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map(role => (
                  <tr key={role.id}>
                    <td>{role.name}</td>
                    <td>{getParentRoleName(role.parent_role_id)}</td>
                    <td>
                      <span className={`status-badge ${role.is_active ? 'active' : 'inactive'}`}>
                        {role.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <span className={`standard-badge ${role.is_standard ? 'standard' : 'custom'}`}>
                        {role.is_standard ? 'Standard' : 'Custom'}
                      </span>
                    </td>
                    <td>{role.created_date_formatted}</td>
                    <td>
                      <div className="action-buttons">
                        {!isAdminRole(role.name) && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleEdit(role)}
                          >
                            Edit
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => handleViewPermissions(role)}
                        >
                          View Permissions
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManager;
